import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { SideNav, SideNavItems, SideNavLink } from '@carbon/react';
import MainSideNavSettings from 'src/components/Layout/MainSideNavSettings';
import { MainNavItem, isMainNavItemActive, mainNav } from 'src/config/mainNav';
import { useTenantContext } from 'src/context/useTenantContext';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useSelfEmployee } from 'src/hooks/useSelfEmployee';
import { useSettingsNav } from 'src/hooks/useSettingsNav';

export function MainSideNav(props: { expanded: any }) {
  const permissions = useAuthPermissions();
  // Only staff with an actual employment record here get the entry, so an owner who was
  // never hired never sees a link to a page that can only tell them they have no record.
  const { employee: selfEmployee } = useSelfEmployee();
  const { sections } = useSettingsNav();
  const { pathname } = useLocation();
  const { buildPath } = useTenantContext();
  const { t } = useTranslation();

  const visible = (item: MainNavItem) => {
    // The Settings entry is the one item whose visibility is derived rather than declared:
    // it appears when the viewer can see any section, which no single capability expresses.
    if (item.pinBottom) return sections.length > 0;
    if (item.requiresSelfEmployee && !selfEmployee) return false;
    if (item.capability && !permissions[item.capability]) return false;
    return true;
  };

  const renderItem = (item: MainNavItem) => (
    <SideNavLink
      key={item.path}
      large
      as={Link}
      to={buildPath(item.path)}
      title={t(item.labelKey)}
      renderIcon={item.icon}
      className={isMainNavItemActive(item, pathname) ? 'cds--side-nav__link--current' : undefined}
    >
      {t(item.labelKey)}
    </SideNavLink>
  );

  return (
    <SideNav isRail addMouseListeners={false} addFocusListeners={false} expanded={props.expanded}>
      <SideNavItems>
        {mainNav.filter((item) => !item.pinBottom && visible(item)).map(renderItem)}
        {/* The user menu moved to the header; this block now anchors only the collapse
            control at the rail's foot via `.cds--side-nav__link--settings`
            (`position: absolute; bottom: 0`). */}
        <div className="cds--side-nav__link--settings">
          {mainNav.filter((item) => item.pinBottom && visible(item)).map(renderItem)}
          <MainSideNavSettings />
        </div>
      </SideNavItems>
    </SideNav>
  );
}
